import { REVIEW_TYPES } from './reviewTypes.js';
import { getRelativeTime } from '../utils/dateUtils.js';

export const ASSIGNMENT_STATUSES = {
  PUBLISHED: 'published',
  COMPLETED: 'completed',
  PENDING_ACTIONS: 'pending_actions',
  IN_PROGRESS: 'in_progress',
  NOT_STARTED: 'not_started'
};

/**
 * The four unfinished states, closest to done first (ADR-098 §2).
 *
 * Mirrors `OPEN_STATUS_ORDER` in radar-api's `utils/assignmentStatus.js`, and
 * for the same reason: the report credits a capped expectation to the
 * assignment furthest along, so the breakdown must be listed in the order it
 * was credited in — otherwise the reader's first line is not the work one
 * action would finish.
 */
export const OPEN_STATUS_ORDER = [
  ASSIGNMENT_STATUSES.PENDING_ACTIONS,
  ASSIGNMENT_STATUSES.IN_PROGRESS,
  ASSIGNMENT_STATUSES.PUBLISHED,
  ASSIGNMENT_STATUSES.NOT_STARTED
];

export const ASSIGNMENT_TYPES = {
  ASSIGNED_TO_ME: 'Assigned to Me',
  ASSIGNED_TO_OTHERS: 'Assigned to Others'
};

export const ABOUT_TYPES = {
  ABOUT_ME: 'About Me',
  ABOUT_OTHERS: 'About Others'
};

export const getStatusBorderColor = (status) => {
  switch (status) {
    case ASSIGNMENT_STATUSES.COMPLETED:
      return '#16A34A';
    case ASSIGNMENT_STATUSES.PENDING_ACTIONS:
      return '#D97706';
    case ASSIGNMENT_STATUSES.IN_PROGRESS:
      return '#2E90FA';
    case ASSIGNMENT_STATUSES.PUBLISHED:
      return '#17A2B8';
    case ASSIGNMENT_STATUSES.NOT_STARTED:
      return '#475569';
  }
} 

export const getStatusColor = (status) => {
  switch (status) {
    case ASSIGNMENT_STATUSES.COMPLETED:
      return '#E2F8E9';
    case ASSIGNMENT_STATUSES.PENDING_ACTIONS:
      return '#FEE2E2';
    case ASSIGNMENT_STATUSES.IN_PROGRESS:
      return '#EBF5FF';
    case ASSIGNMENT_STATUSES.PUBLISHED:
      return '#E0F7FA';
    case ASSIGNMENT_STATUSES.NOT_STARTED:
      return '#F1F5F9';
  }
};

export const getStatusTextColor = (status) => {
  switch (status) {
    case ASSIGNMENT_STATUSES.COMPLETED:
      return '#16A34A';
    case ASSIGNMENT_STATUSES.PENDING_ACTIONS:
      return '#D97706';
    case ASSIGNMENT_STATUSES.IN_PROGRESS:
      return '#2E90FA';
    case ASSIGNMENT_STATUSES.PUBLISHED:
      return '#17A2B8';
    case ASSIGNMENT_STATUSES.NOT_STARTED:
      return '#475569';
  }
};

export const getStatusText = (status) => {
  switch (status) {
    case ASSIGNMENT_STATUSES.COMPLETED:
      return 'Completed';
    case ASSIGNMENT_STATUSES.PENDING_ACTIONS:
      return 'Pending Actions';
    case ASSIGNMENT_STATUSES.IN_PROGRESS:
      return 'In Progress';
    case ASSIGNMENT_STATUSES.PUBLISHED:
      return 'Published';
    case ASSIGNMENT_STATUSES.NOT_STARTED:
      return 'Not Started';
  }
};

export const getStatusDescription = (status) => {
  switch (status) {
    case ASSIGNMENT_STATUSES.COMPLETED:
      return 'All reviews have been completed, feedback and actions have been provided';
    case ASSIGNMENT_STATUSES.PENDING_ACTIONS:
      return 'Reviews are complete but the START/STOP/CONTINUE actions from the Representative are still pending';
    case ASSIGNMENT_STATUSES.IN_PROGRESS:
      return 'One or more reviewers have started working on this assignment';
    // Was missing, so every Published chip fell through to "Unknown status"
    // and rendered without a tooltip. Surfaced by ADR-098's panel, which shows
    // all four open statuses side by side.
    case ASSIGNMENT_STATUSES.PUBLISHED:
      return 'Assigned and visible to its reviewers, but nobody has started yet';
    case ASSIGNMENT_STATUSES.NOT_STARTED:
      return 'No reviewers have begun working on this assignment yet';
    default:
      return 'Unknown status';
  }
};

export const getReviewerStatus = (reviewer) => {

  // Apply new logic only for SEQUENTIAL review type
  if (reviewer.reviewType === REVIEW_TYPES.SEQUENTIAL) {

    const isAssignmentCompleted = reviewer.assignment?.reviewsCompletedAt;

    // If completed, started, and feedback attached - fully completed
    if (reviewer.completedAt && reviewer.feedbackAttachedAt && reviewer.startedAt) {
      return ASSIGNMENT_STATUSES.COMPLETED;
    }
    
    // If completed and started but no feedback attached
    if (reviewer.completedAt && reviewer.startedAt && !reviewer.feedbackAttachedAt) {
      // Self assessment (order 1) is considered completed even without feedback
      if (reviewer.order === 1) {
        return ASSIGNMENT_STATUSES.COMPLETED;
      }

      if (isAssignmentCompleted) {
        return ASSIGNMENT_STATUSES.COMPLETED;
      }
      // Others need pending actions for feedback
      return ASSIGNMENT_STATUSES.PENDING_ACTIONS;
    }
    
    // If only started
    if (reviewer.startedAt) {
      return ASSIGNMENT_STATUSES.IN_PROGRESS;
    }
    
    // Not started
    return ASSIGNMENT_STATUSES.NOT_STARTED;
  }
  
  // Original logic for non-SEQUENTIAL review types
  return reviewer.completedAt ? ASSIGNMENT_STATUSES.COMPLETED :
           reviewer.startedAt ? ASSIGNMENT_STATUSES.IN_PROGRESS :
           ASSIGNMENT_STATUSES.NOT_STARTED;
};

export const getReviewTiming = (reviewer) => {
  if (!reviewer.assignedAt) return null;
  
  // For sequential reviews, handle feedback timing
  if (reviewer.reviewType === REVIEW_TYPES.SEQUENTIAL) {
    const isAssignmentCompleted = reviewer.assignment?.reviewsCompletedAt;
    
    if (reviewer.feedbackAttachedAt) {
      // Final state - feedback has been attached
      return `Feedback provided ${getRelativeTime(reviewer.feedbackAttachedAt)}`;
    } else if (reviewer.completedAt) {
      // Review completed but waiting for feedback (except for self-assessment)
      if (reviewer.order === 1) {
        // Self-assessment doesn't need feedback
        return `Completed ${getRelativeTime(reviewer.completedAt)}`;
      } else {
        if (isAssignmentCompleted) {
          return `Completed ${getRelativeTime(reviewer.completedAt)}`;
        } else {
          // Reviewer needs to provide feedback to the assessed person
          return `Feedback pending since ${getRelativeTime(reviewer.completedAt)}`;
        }
      }
    } else if (reviewer.startedAt) {
      // Review in progress
      return `Started ${getRelativeTime(reviewer.startedAt)}`;
    }
    // Not started yet
    return `Assigned ${getRelativeTime(reviewer.assignedAt)}`;
  }
  
  // For parallel reviews, use original logic
  if (reviewer.completedAt) {
    return `Completed ${getRelativeTime(reviewer.completedAt)}`;
  } else if (reviewer.startedAt) {
    return `Started ${getRelativeTime(reviewer.startedAt)}`;
  }
  return `Assigned ${getRelativeTime(reviewer.assignedAt)}`;
};

export const getReviewerStatusText = (reviewer) => {
  return getStatusText(getReviewerStatus(reviewer));
};

export const getReviewerStatusColor = (reviewer) => {
  return getStatusColor(getReviewerStatus(reviewer));
};

export const getReviewerStatusBorderColor = (reviewer) => {
  return getStatusBorderColor(getReviewerStatus(reviewer));
};

export const getReviewerStatusTextColor = (reviewer) => {
  return getStatusTextColor(getReviewerStatus(reviewer));
};

// from the perspective of was published to in progress
export const getIndividualAssignmentStatus = (assignment) => {
  if (assignment.completedAt
    || (assignment.feedbackAttachedAt && assignment.reviewsCompletedAt)) {
    return ASSIGNMENT_STATUSES.COMPLETED;
  }

  if (assignment.assignedAt && !assignment.completedAt) {
    return ASSIGNMENT_STATUSES.IN_PROGRESS;
  }

  return ASSIGNMENT_STATUSES.NOT_STARTED;
}

export const getAssignmentStatus = (assignment) => {
  if (assignment.completedAt
    || (assignment.feedbackAttachedAt && assignment.reviewsCompletedAt)) {
    return ASSIGNMENT_STATUSES.COMPLETED;
  }

  if (!assignment.completedAt
      && assignment.reviewsCompletedAt
      && !assignment.feedbackAttachedAt) {
    return ASSIGNMENT_STATUSES.PENDING_ACTIONS;
  }

  // an issue here as we are obfuscating the rest of the reviewers
  // so we are setting the status wrong
  // we have two options:
  // 1. we can modify the api and for the obfuscated reviewers we can set the startedAt if any of the reviewers has started
  // 2. we can use the completionProgress.totalPercentage and if it is > 0 we have progress
  const hasStartedReviewer = assignment.reviewers?.some(reviewer => reviewer.startedAt);
  if (hasStartedReviewer) {
    return ASSIGNMENT_STATUSES.IN_PROGRESS;
  }

  if (assignment.assignedAt
    && !assignment.completedAt
    && !assignment.feedbackAttachedAt
    && !assignment.reviewsCompletedAt) {
    return ASSIGNMENT_STATUSES.PUBLISHED;
  }

  return ASSIGNMENT_STATUSES.NOT_STARTED;
};

/**
 * Find the current active reviewer in a SEQUENTIAL chain. Mirrors the primary
 * rules of `findCurrentReviewer` in radar-api/src/utils/reviewerHelpers.js: the
 * first reviewer that is assigned/started but not completed, then assigned but
 * not completed, then a non-first reviewer completed but awaiting feedback
 * attachment. Returns null when the chain is finished.
 *
 * @param {Array} reviewers - reviewer assignment objects ({ order, assignedAt,
 *   startedAt, completedAt, feedbackAttachedAt }).
 * @returns {Object|null}
 */
const findCurrentSequentialReviewer = (reviewers) => {
  if (!reviewers || reviewers.length === 0) {
    return null;
  }
  const orders = reviewers
    .map((r) => r.order)
    .filter((o) => o !== null && o !== undefined);
  const minOrder = orders.length > 0 ? Math.min(...orders) : null;

  // Rule 1: assigned, started, not completed.
  let current = reviewers.find((r) => r.assignedAt && r.startedAt && !r.completedAt);
  if (current) {
    return current;
  }
  // Rule 2: assigned, not completed.
  current = reviewers.find((r) => r.assignedAt && !r.completedAt);
  if (current) {
    return current;
  }
  // Rule 2.5: non-first reviewer completed but feedback not yet attached.
  current = reviewers.find((r) => r.order !== minOrder && r.completedAt && !r.feedbackAttachedAt);
  if (current) {
    return current;
  }
  // Rule 3: nobody touched yet -> the first (lowest-order) reviewer.
  const allUntouched = reviewers.every((r) => !r.assignedAt && !r.startedAt && !r.completedAt);
  if (allUntouched) {
    return reviewers.reduce((a, b) => ((a.order ?? Infinity) <= (b.order ?? Infinity) ? a : b));
  }
  // Otherwise the chain is finished.
  return null;
};

/**
 * ADR-003 Decision 8: whether a reviewer's competency review may be reopened
 * (un-submitted so the matrix becomes editable again).
 *
 * Mirrors `reopenReview` in radar-api feedbackSessionModificationService.js; the
 * backend remains authoritative. This covers the REVIEW-level conditions only:
 *   1. the review is submitted (completedAt is set), and
 *   2. for SEQUENTIAL reviews, only the ACTIVE step (or, once the chain is
 *      finished, the last/highest-order step) is eligible — earlier completed
 *      steps are locked because a later reviewer has built on them.
 * PARALLEL reviewers pass here as soon as they are submitted; the additional
 * "assignment must not be completed" gate is applied by the caller, which has the
 * assignment status (reopen the Start/Stop/Continue actions first otherwise).
 *
 * @param {Object} reviewer - the reviewer assignment object being acted on.
 * @param {Array} siblingReviewers - all reviewer assignment objects on the
 *   assignment (used to locate the current sequential step).
 * @returns {boolean}
 */
export const canReopenReview = (reviewer, siblingReviewers = []) => {
  if (!reviewer || !reviewer.completedAt) {
    return false;
  }
  if (reviewer.reviewType !== REVIEW_TYPES.SEQUENTIAL) {
    return true;
  }
  const current = findCurrentSequentialReviewer(siblingReviewers);
  if (current) {
    return reviewer.order === current.order;
  }
  const maxOrder = Math.max(...siblingReviewers.map((r) => r.order ?? -Infinity));
  return reviewer.order === maxOrder;
};

/**
 * ADR-003 Decision 9: whether a reviewer's competency review may be RESET back to
 * "Not Started" (wiping the reviewer's responses and discarding any in-progress
 * Start/Stop/Continue draft).
 *
 * Mirrors `resetReview` in radar-api feedbackSessionModificationService.js; the
 * backend remains authoritative. Conditions:
 *   1. the review has been worked on (startedAt OR completedAt is set), and
 *   2. for SEQUENTIAL reviews, the reviewer must be: the ACTIVE step; OR the step
 *      immediately before the active step (active.order - 1) WHEN the active step
 *      has not started yet; OR — once the chain is finished — the last
 *      (highest-order) step.
 * PARALLEL reviewers pass here as soon as they have started/completed; the
 * additional "assignment must not be completed" gate is applied by the caller,
 * which has the assignment status (reopen the Start/Stop/Continue actions first
 * otherwise).
 *
 * @param {Object} reviewer - the reviewer assignment object being acted on.
 * @param {Array} siblingReviewers - all reviewer assignment objects on the
 *   assignment (used to locate the current sequential step).
 * @returns {boolean}
 */
export const canResetReview = (reviewer, siblingReviewers = []) => {
  if (!reviewer || (!reviewer.startedAt && !reviewer.completedAt)) {
    return false;
  }
  if (reviewer.reviewType !== REVIEW_TYPES.SEQUENTIAL) {
    return true;
  }
  const current = findCurrentSequentialReviewer(siblingReviewers);
  if (current) {
    if (reviewer.order === current.order) {
      return true;
    }
    const activeNotStarted = !current.startedAt && !current.completedAt;
    return activeNotStarted && reviewer.order === current.order - 1;
  }
  const maxOrder = Math.max(...siblingReviewers.map((r) => r.order ?? -Infinity));
  return reviewer.order === maxOrder;
};

/**
 * ADR-003 Decision 10: whether an INTERIM Sequential reviewer's per-step
 * Start/Stop/Continue may be reopened (so they can edit and resubmit it).
 *
 * Mirrors `reopenReviewerActions` in radar-api feedbackSessionModificationService.js;
 * the backend remains authoritative. Conditions:
 *   1. SEQUENTIAL, and the reviewer is neither the self-assessment (lowest order)
 *      nor the last (highest order) step — the final reviewer's SSC is reopened at
 *      the assignment level (the assignee's "Reopen Start/Stop/Continue"); and
 *   2. the reviewer submitted their SSC (feedbackAttachedAt is set); and
 *   3. the reviewer is exactly `activeStep.order - 1` where the active step has not
 *      started yet (no startedAt and no completedAt) — once the next reviewer
 *      starts they have built on this SSC, so it locks.
 *
 * @param {Object} reviewer - the reviewer assignment object being acted on.
 * @param {Array} siblingReviewers - all reviewer assignment objects on the
 *   assignment (used to locate the current sequential step and the order bounds).
 * @returns {boolean}
 */
export const canReopenReviewerActions = (reviewer, siblingReviewers = []) => {
  if (!reviewer || reviewer.reviewType !== REVIEW_TYPES.SEQUENTIAL) {
    return false;
  }
  if (!reviewer.feedbackAttachedAt) {
    return false;
  }
  const orders = siblingReviewers
    .map((r) => r.order)
    .filter((o) => o !== null && o !== undefined);
  if (orders.length === 0) {
    return false;
  }
  const minOrder = Math.min(...orders);
  const maxOrder = Math.max(...orders);
  // Self-assessment (min) has no SSC; the last step (max) is reopened at the
  // assignment level.
  if (reviewer.order === minOrder || reviewer.order === maxOrder) {
    return false;
  }
  const current = findCurrentSequentialReviewer(siblingReviewers);
  if (!current) {
    return false;
  }
  const activeNotStarted = !current.startedAt && !current.completedAt;
  return activeNotStarted && reviewer.order === current.order - 1;
};

/**
 * Whether an assignment has enough finished material for its feedback report to
 * be worth opening. This is a DATA-READINESS gate, not a permission — the API
 * (`getAssignmentReport`) is authoritative on who may read what, and applies
 * its own rules (manager/admin/PP access, own-Parallel-before-reviewsCompletedAt,
 * reviewer anonymisation, draft suppression).
 *
 * True when any of:
 *   - the assignment is completed;
 *   - all reviews are in (`reviewsCompletedAt`, i.e. PENDING_ACTIONS) — the
 *     individual may read it early to prepare for the discussion with their
 *     Representative (ADR "Allow Employees to View Feedback Report During
 *     PENDING_ACTIONS"); or
 *   - it is SEQUENTIAL and at least one step has been submitted. The
 *     self-assessment is first in a chain, so in practice this usually means
 *     "the individual has assessed themselves" — deliberately counted, because
 *     a self-assessment is real data for whoever may access the report.
 *
 * Extracted 2026-08-10 from three copies that had drifted: `SessionGrid` was
 * missing the `reviewsCompletedAt` clause, so the same Parallel assignment at
 * PENDING_ACTIONS was viewable on Feedback Results and My Feedback Results but
 * not on Individual Assignments.
 *
 * NOTE: the SEQUENTIAL clause reads reviewer `completedAt`. Both list selects in
 * radar-api `assignmentReportService` must ship that field on their lean
 * (`includeReviewers: false`) branch or this silently returns false.
 *
 * @param {Object} assignment - needs completedAt / reviewsCompletedAt and,
 *   ideally, a `reviewers` array carrying reviewType + completedAt.
 * @returns {boolean}
 */
export const canViewAssignmentReport = (assignment) => {
  if (!assignment) {
    return false;
  }
  if (assignment.completedAt || assignment.reviewsCompletedAt) {
    return true;
  }

  const reviewers = assignment.reviewers;
  if (!Array.isArray(reviewers) || reviewers.length === 0) {
    return false;
  }
  if (reviewers[0].reviewType !== REVIEW_TYPES.SEQUENTIAL) {
    return false;
  }
  return reviewers.some((reviewer) => !!reviewer.completedAt);
};

export const getAssignmentStatusColor = (assignment) => {
  return getStatusColor(getAssignmentStatus(assignment));
};

export const getAssignmentStatusTextColor = (assignment) => {
  return getStatusTextColor(getAssignmentStatus(assignment));
};

export const getAssignmentStatusBorderColor = (assignment) => {
  return getStatusBorderColor(getAssignmentStatus(assignment));
};

export const getAssignmentStatusText = (assignment) => {
  return getStatusText(getAssignmentStatus(assignment));
};

// User-centered assignment status badges
export const ASSIGNMENT_BADGES = {
  ACTION_REQUIRED: 'Action Required',
  IN_PROGRESS: 'In Progress',
  MY_PART_DONE: 'My Part Done', 
  WAITING_FOR_REVIEWER: 'Waiting for Reviewer',
  WAITING_YOUR_TURN: 'Waiting Your Turn',
  COMPLETED: 'Completed',
  // ADR-014 §10k: the parent session was closed while this assignment was still
  // open, so it is frozen where it stopped and can never be finished.
  //
  // The label names the ASSIGNMENT, not the session — this badge occupies the
  // assignment-status slot, where "Completed" is its peer. (It read "Session
  // Closed" until 2026-08-10; once §10j put a real session chip on the same
  // cards, that both duplicated it and answered the wrong question.)
  //
  // Past tense is load-bearing: "Incomplete" alone reads as a live item to
  // clear next to Action Required / In Progress, and slate does not rescue it
  // because WAITING_YOUR_TURN is grey too.
  LEFT_INCOMPLETE: 'Left Incomplete'
};

export const getAssignmentStatusMyPerspective = (assignment, currentUserId) => {

  // Find current user's reviewer record
  const myReviewer = assignment.reviewers?.find(r => r.employeeId === currentUserId);
  
  if (!myReviewer) {
    // User is not a reviewer, fallback to general status
    const fallbackResult = getAssignmentStatus(assignment) === ASSIGNMENT_STATUSES.COMPLETED 
      ? ASSIGNMENT_BADGES.COMPLETED 
      : ASSIGNMENT_BADGES.WAITING_YOUR_TURN;
    return fallbackResult;
  }

  // Determine if this is a sequential or parallel review
  const isSequential = assignment.reviewers?.some(r => r.reviewType === REVIEW_TYPES.SEQUENTIAL);
  
  // Determine if assignment is about me (I'm the subject being reviewed)
  const isAboutMe = assignment.employee?.id === currentUserId;
  
  // Get my status
  const myStatus = getReviewerStatus(myReviewer);
  
  // ADR-014 §10p: a frozen assignment is decided FIRST, because the two
  // completion short-circuits below do not agree on what "complete" means.
  //
  // `completionProgress.completedPercentage` comes from
  // `calculateCompletionProgress`, whose legacy branch returns 100 as soon as
  // `reviewsCompletedAt` is set and no reviewer carries `feedbackAttachedAt` —
  // which is exactly the shape of PENDING_ACTIONS. So a parallel assignment
  // still awaiting its Start/Stop/Continue actions reported 100%, short-
  // circuited to COMPLETED, and never reached the frozen check below it.
  //
  // The guard is `getAssignmentStatus`, matching the `personalFrozen`
  // derivation the card surfaces use, so a genuinely completed assignment in a
  // closed session still reads COMPLETED.
  const isParentSessionClosed = !!(assignment.isLocked || assignment.feedbackSession?.completedAt);
  if (isParentSessionClosed
      && getAssignmentStatus(assignment) !== ASSIGNMENT_STATUSES.COMPLETED) {
    return ASSIGNMENT_BADGES.LEFT_INCOMPLETE;
  }

  // Check if assignment is fully completed - PRIORITY CHECK
  if (assignment.completionProgress?.completedPercentage === 100) {
    return ASSIGNMENT_BADGES.COMPLETED;
  }

  // Fallback check for assignment completion flags
  if (assignment.completedAt || (assignment.feedbackAttachedAt && assignment.reviewsCompletedAt)) {
    return ASSIGNMENT_BADGES.COMPLETED;
  }

  if (isSequential) {
    if (isAboutMe) {
      // Sequential + About Me + Self-evaluator
      if (myStatus === ASSIGNMENT_STATUSES.NOT_STARTED) {
        return ASSIGNMENT_BADGES.ACTION_REQUIRED;
      }
      
      if (myStatus === ASSIGNMENT_STATUSES.IN_PROGRESS) {
        return ASSIGNMENT_BADGES.IN_PROGRESS;
      }
      
      if (myStatus === ASSIGNMENT_STATUSES.COMPLETED) {
        // Check if others have started (use completion progress > my contribution)
        // Since I'm the self-evaluator, if totalPercentage > my completed portion, others started
        const othersStarted = assignment.completionProgress?.totalPercentage > assignment.completionProgress?.completedPercentage;

        if (othersStarted) {
          return ASSIGNMENT_BADGES.WAITING_FOR_REVIEWER;
        } else {
          return ASSIGNMENT_BADGES.MY_PART_DONE;
        }
      }
    } else {
      // Sequential + Not About Me + Reviewer
      // Check if I'm the current reviewer (previous reviewers completed)
      const previousReviewers = assignment.reviewers?.filter(r => r.order < myReviewer.order) || [];
      const allPreviousCompleted = previousReviewers.every(r => getReviewerStatus(r) === ASSIGNMENT_STATUSES.COMPLETED);
      
      if (!allPreviousCompleted) {
        return ASSIGNMENT_BADGES.WAITING_YOUR_TURN;
      }
      
      // I'm the current reviewer
      if (myStatus === ASSIGNMENT_STATUSES.NOT_STARTED) {
        return ASSIGNMENT_BADGES.ACTION_REQUIRED;
      }
      
      if (myStatus === ASSIGNMENT_STATUSES.IN_PROGRESS) {
        return ASSIGNMENT_BADGES.IN_PROGRESS;
      }

      if (myStatus === ASSIGNMENT_STATUSES.PENDING_ACTIONS) {
        //return ASSIGNMENT_BADGES.WAITING_YOUR_TURN;
        return ASSIGNMENT_BADGES.IN_PROGRESS;
      }
      
      if (myStatus === ASSIGNMENT_STATUSES.COMPLETED || myStatus === ASSIGNMENT_STATUSES.PENDING_ACTIONS) {
        return ASSIGNMENT_BADGES.MY_PART_DONE;
      }
    }
  } else {
    // Parallel review
    if (myStatus === ASSIGNMENT_STATUSES.NOT_STARTED) {
      return ASSIGNMENT_BADGES.ACTION_REQUIRED;
    }
    
    if (myStatus === ASSIGNMENT_STATUSES.IN_PROGRESS) {
      return ASSIGNMENT_BADGES.IN_PROGRESS;
    }
    
    if (myStatus === ASSIGNMENT_STATUSES.COMPLETED || myStatus === ASSIGNMENT_STATUSES.PENDING_ACTIONS) {
      return ASSIGNMENT_BADGES.MY_PART_DONE;
    }
  }
  
  // Default fallback for in-progress states
  return ASSIGNMENT_BADGES.ACTION_REQUIRED;
};

export const getAssignmentTooltipMyPerspective = (badge) => {
  switch (badge) {
    case ASSIGNMENT_BADGES.ACTION_REQUIRED:
      return "It's your turn to complete the assessment.";
    case ASSIGNMENT_BADGES.IN_PROGRESS:
      return "You're currently working on your feedback.";
    case ASSIGNMENT_BADGES.MY_PART_DONE:
      return "You've completed your part. Waiting for others to finish.";
    case ASSIGNMENT_BADGES.WAITING_FOR_REVIEWER:
      return "Self-assessment done. Reviewer is evaluating now.";
    case ASSIGNMENT_BADGES.WAITING_YOUR_TURN:
      return "You'll be invited to review once the current step is complete.";
    case ASSIGNMENT_BADGES.COMPLETED:
      return "All steps are finished for this assignment.";
    case ASSIGNMENT_BADGES.LEFT_INCOMPLETE:
      return "The session was closed before this assignment was finished. It is preserved as-is and can no longer be worked on.";
    default:
      return "";
  }
};

export const getBadgeColor = (badge) => {
  switch (badge) {
    case ASSIGNMENT_BADGES.ACTION_REQUIRED:
      return '#FEF3C7'; // Light amber
    case ASSIGNMENT_BADGES.IN_PROGRESS:
      return '#EBF5FF'; // Light blue
    case ASSIGNMENT_BADGES.MY_PART_DONE:
      return '#F0F9F4'; // Light green
    case ASSIGNMENT_BADGES.WAITING_FOR_REVIEWER:
      return '#F3E8FF'; // Light purple
    case ASSIGNMENT_BADGES.WAITING_YOUR_TURN:
      return '#F8FAFC'; // Light gray
    case ASSIGNMENT_BADGES.COMPLETED:
      return '#E2F8E9'; // Green
    case ASSIGNMENT_BADGES.LEFT_INCOMPLETE:
      return '#F1F5F9'; // Neutral slate — inactive/frozen (ADR-014)
    default:
      return '#F1F5F9'; // Default gray
  }
};

export const getBadgeTextColor = (badge) => {
  switch (badge) {
    case ASSIGNMENT_BADGES.ACTION_REQUIRED:
      return '#D97706'; // Amber
    case ASSIGNMENT_BADGES.IN_PROGRESS:
      return '#2E90FA'; // Blue
    case ASSIGNMENT_BADGES.MY_PART_DONE:
      return '#059669'; // Green
    case ASSIGNMENT_BADGES.WAITING_FOR_REVIEWER:
      return '#7C3AED'; // Purple
    case ASSIGNMENT_BADGES.WAITING_YOUR_TURN:
      return '#64748B'; // Gray
    case ASSIGNMENT_BADGES.COMPLETED:
      return '#16A34A'; // Green
    case ASSIGNMENT_BADGES.LEFT_INCOMPLETE:
      return '#64748B'; // Neutral slate — inactive/frozen (ADR-014)
    default:
      return '#475569'; // Default gray
  }
};

export const getBadgeBorderColor = (badge) => {
  switch (badge) {
    case ASSIGNMENT_BADGES.ACTION_REQUIRED:
      return '#D97706'; // Amber
    case ASSIGNMENT_BADGES.IN_PROGRESS:
      return '#2E90FA'; // Blue
    case ASSIGNMENT_BADGES.MY_PART_DONE:
      return '#059669'; // Green
    case ASSIGNMENT_BADGES.WAITING_FOR_REVIEWER:
      return '#7C3AED'; // Purple
    case ASSIGNMENT_BADGES.WAITING_YOUR_TURN:
      return '#64748B'; // Gray
    case ASSIGNMENT_BADGES.COMPLETED:
      return '#16A34A'; // Green
    case ASSIGNMENT_BADGES.LEFT_INCOMPLETE:
      return '#94A3B8'; // Neutral slate — inactive/frozen (ADR-014)
    default:
      return '#475569'; // Default gray
  }
};